import { useMemo } from 'react';

import { getTime, sub } from 'date-fns';
import isEqual from 'lodash/isEqual';
import { useGetList, useListContext } from 'react-admin';
import { DEFAULT_TIMEZONE } from 'shared/mui/DatePicker/config/defaultValues';
import {
  DateRangeTimezoneValue,
  DateRangeValue,
} from 'shared/mui/DatePicker/DateTimeRangeTimezonePicker/DateTimeRangeTimezonePicker';
import { getAppliedTimezone } from 'shared/mui/DatePicker/utils/getAppliedTimezone';
import { useFilters, UseFiltersReturnValues } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import { Merchant } from 'types/embedded/merchant';
import { cleanEmpty } from 'utils/cleanEmpty';
import { formatInputToTimestamp } from 'utils/date';

export type DashboardFilterValues = {
  appliedDate: DateRangeValue;
  timezone: DateRangeTimezoneValue;
  provider?: string[];
  gateway?: string[];
  merchant: string[];
  currency: string;
  periodFrom: number | null;
  periodTo: number | null;
};

type UseDashboardFiltersReturnType = UseFiltersReturnValues<DashboardFilterValues> & {
  isFilterReady: boolean;
  isFilterPresetApplied: boolean;
};

const MERCHANT_LIST_PAGINATION = { page: 1, perPage: 100 };
const DEFAULT_CURRENCY_ALPHA_CODE = 'USD';

export function useDashboardFilters(isEmptyMerchantAccess: boolean): UseDashboardFiltersReturnType {
  const { filterValues } = useListContext();
  const isEmptyUrlFilter = Object.keys(cleanEmpty(filterValues)).length === 0;

  const enableFilterPreset = !isEmptyMerchantAccess && isEmptyUrlFilter;

  const { data: merchants } = useGetList<Merchant>(
    'merchants',
    {
      pagination: MERCHANT_LIST_PAGINATION,
      filter: { archive: false },
    },
    { enabled: !isEmptyMerchantAccess },
  );

  const freshLast24h: DateRangeValue = useMemo(() => {
    const now = new Date();
    return [getTime(sub(now, { hours: 24 })), getTime(now)];
  }, []);

  const freshPeriod = useMemo(
    () => ({
      periodFrom: formatInputToTimestamp(freshLast24h[0], { unit: 'seconds' }),
      periodTo: formatInputToTimestamp(freshLast24h[1], { unit: 'seconds' }),
    }),
    [freshLast24h],
  );

  const initialFilterValues = {
    appliedDate: freshLast24h,
    ...freshPeriod,
    timezone: DEFAULT_TIMEZONE,
    merchant: merchants?.map((merchant) => merchant.id) || [],
    currency: DEFAULT_CURRENCY_ALPHA_CODE,
    provider: [],
    gateway: [],
  };

  const defaultFilterValues: DashboardFilterValues = {
    appliedDate: initialFilterValues.appliedDate,
    periodFrom: initialFilterValues.periodFrom,
    periodTo: initialFilterValues.periodTo,
    timezone: filterValues.timezone ?? initialFilterValues.timezone,
    merchant: filterValues.merchant || initialFilterValues.merchant,
    currency: filterValues.currency || initialFilterValues.currency,
    provider: filterValues.provider || initialFilterValues.provider,
    gateway: filterValues.gateway || initialFilterValues.gateway,
  };

  const { appliedFilters, ...rest } = useFilters<DashboardFilterValues>({
    defaultValues: defaultFilterValues,
    resetValues: initialFilterValues,
    mode: 'all',
  });

  const isFilterReady =
    !enableFilterPreset || (Boolean(merchants) && isEqual(appliedFilters, defaultFilterValues));

  useSyncFilter({
    enabled: isFilterReady,
    appliedFilters,
    transform: (appliedFilters) => {
      const { appliedDate = [null, null], timezone, ...restFilters } = appliedFilters;

      return cleanEmpty({
        ...restFilters,
        periodFrom: formatInputToTimestamp(appliedDate?.[0], { unit: 'seconds' }),
        periodTo: formatInputToTimestamp(appliedDate?.[1], { unit: 'seconds' }),
        timezone: getAppliedTimezone(timezone, appliedDate),
      });
    },
  });

  return {
    appliedFilters,
    isFilterReady,
    isFilterPresetApplied: isEqual(defaultFilterValues, initialFilterValues),
    ...rest,
  };
}
